"use client";

import { CheckCircle } from "lucide-react";
import type { AnswerValue, Question } from "../types";

interface QuestionFieldProps {
  question: Question;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
  hidden?: boolean;
}

/**
 * Long option lists stack; `columns: 2` in the catalog lays them out in a
 * two-column grid. Deliberately opt-in per question rather than automatic —
 * options carrying a long sublabel need the full width to stay readable, and
 * the catalog author is the one who knows which those are.
 */
function optionListClass(question: Question): string {
  return question.columns === 2
    ? "grid grid-cols-1 sm:grid-cols-2 gap-2 items-start"
    : "space-y-2";
}

const inputBase =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-abis-primary focus:ring-2 focus:ring-abis-primary/20 outline-none transition-all duration-200";
const labelBase = "block text-sm font-semibold text-gray-800 mb-1.5";
const helpBase = "text-xs text-gray-500 mb-2";

function OptionCard({
  selected,
  shape,
  label,
  sublabel,
  onClick,
  disabled,
}: {
  selected: boolean;
  shape: "radio" | "checkbox";
  label: string;
  sublabel?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      disabled={disabled}
      className={`w-full text-left flex items-start gap-3 rounded-lg border px-3.5 py-2.5 transition-all duration-150 ${
        selected
          ? "border-abis-primary bg-abis-primary/5"
          : disabled
            ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
            : "border-gray-200 bg-white hover:border-abis-primary/40"
      }`}
    >
      <span
        className={`mt-0.5 flex-shrink-0 flex items-center justify-center w-4.5 h-4.5 ${
          shape === "radio" ? "rounded-full" : "rounded"
        } border-2 ${selected ? "border-abis-primary bg-abis-primary" : "border-gray-300 bg-white"}`}
        style={{ width: 18, height: 18 }}
      >
        {selected ? (
          shape === "radio" ? (
            <span className="w-1.5 h-1.5 rounded-full bg-white" />
          ) : (
            <CheckCircle className="w-3 h-3 text-white" />
          )
        ) : null}
      </span>
      <span>
        <span className="block text-sm font-medium text-gray-900">{label}</span>
        {sublabel ? <span className="block text-xs text-gray-500 mt-0.5">{sublabel}</span> : null}
      </span>
    </button>
  );
}

export default function QuestionField({ question, value, onChange, hidden }: QuestionFieldProps) {
  const fieldId = `field-${question.id}`;

  return (
    <div hidden={hidden} className="mb-5">
      <label htmlFor={fieldId} className={labelBase}>
        {question.label}
        {question.required ? <span className="text-abis-error"> *</span> : null}
      </label>
      {question.help ? <p className={helpBase}>{question.help}</p> : null}

      {question.type === "text" ? (
        <input
          id={fieldId}
          type="text"
          placeholder={question.placeholder}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputBase}
        />
      ) : null}

      {question.type === "number" ? (
        <input
          id={fieldId}
          type="number"
          min={0}
          value={typeof value === "number" ? value : ""}
          onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
          className={inputBase}
        />
      ) : null}

      {question.type === "date" ? (
        <input
          id={fieldId}
          type="date"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputBase}
        />
      ) : null}

      {question.type === "textarea" ? (
        <textarea
          id={fieldId}
          rows={3}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputBase} resize-y`}
        />
      ) : null}

      {question.type === "color" ? (
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label={`${question.label} Farbwähler`}
            value={typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value) ? value : "#003188"}
            onChange={(e) => onChange(e.target.value)}
            className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5 flex-shrink-0"
          />
          <input
            id={fieldId}
            type="text"
            placeholder={question.placeholder ?? "#003188"}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputBase} font-mono`}
          />
        </div>
      ) : null}

      {question.type === "single" && question.options ? (
        <div className={optionListClass(question)} role="radiogroup" aria-labelledby={fieldId}>
          {question.options.map((option) => (
            <OptionCard
              key={option.value}
              shape="radio"
              selected={value === option.value}
              label={option.label}
              sublabel={option.sublabel}
              onClick={() => onChange(option.value)}
            />
          ))}
        </div>
      ) : null}

      {question.type === "multi" && question.options ? (
        <div>
          {(() => {
            const current = Array.isArray(value) ? value : [];
            const max = question.maxSelections;
            const atCap = max !== undefined && current.length >= max;
            return (
              <>
                <div className={optionListClass(question)}>
                {question.options.map((option) => {
                  const selected = current.includes(option.value);
                  return (
                    <OptionCard
                      key={option.value}
                      shape="checkbox"
                      selected={selected}
                      // At the cap, unselected options lock — deselect first to swap.
                      disabled={!selected && atCap}
                      label={option.label}
                      sublabel={option.sublabel}
                      onClick={() =>
                        onChange(
                          selected
                            ? current.filter((v) => v !== option.value)
                            : [...current, option.value]
                        )
                      }
                    />
                  );
                })}
                </div>
                {max !== undefined ? (
                  <p className={`text-xs mt-1 ${atCap ? "text-abis-primary font-medium" : "text-gray-400"}`}>
                    {current.length}/{max} ausgewählt
                  </p>
                ) : null}
              </>
            );
          })()}
        </div>
      ) : null}
    </div>
  );
}
